import React from 'react'
// components
import { TableRow } from '@mui/material'
import { HoverablePopover, Link } from 'components/UI'
// constants
import { KPI_RESULT_TYPE_VALUES, Modals } from 'constants/index'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// constants
import { Chip, ICON_STYLE, ReviewIcon, TableCell, TEXT_STYLE } from 'themes/views/tables'
// themes
import { EllipsizedText } from 'themes/views/generic'
// icons
import WatchLaterIcon from '@mui/icons-material/WatchLaterOutlined'
// types
import { IField, IPlanKpi } from 'types/graphql'
// lodash
import last from 'lodash/fp/last'
import find from 'lodash/fp/find'
import pipe from 'lodash/fp/pipe'
import getFp from 'lodash/fp/get'
import orderBy from 'lodash/fp/orderBy'
// utils
import { isUuid } from 'utils/uuid'
import { getEntityStatus } from 'utils/fields'
import { getChipColor, getClosestRange, getClosestReview, getOwnedBy, getPlanFromEntity, safeGetOr } from 'utils/index'

interface Props {
  data: IPlanKpi
  fields: IField[]
}

const Row: React.FC<Props> = ({ data, fields }) => {
  const { showModal } = useShowModal({ modalType: Modals.KPI_MINIMIZED_MODAL })

  const {
    id,
    recurrence,
    resultType,
    description,
    ownedByUser,
    resultTarget,
    fieldValueMap,
    ownedByPosition,
    resultTypeScale
  } = data

  const plan = getPlanFromEntity(data)
  const ownedBy = getOwnedBy(ownedByPosition, ownedByUser)
  const lastResult = getLastResult(resultTarget, resultTypeScale, resultType)
  const closestRange = getClosestRange(recurrence, resultTarget)
  const closestReview = getClosestReview(data)
  const resultDueDate = closestRange && closestRange.dueDate

  const chipColor = getChipColor(resultDueDate, closestRange?.status)
  const chipLabel = closestRange ? closestRange.dueDate.format('D MMM YY') : 'No data set'

  const showKPIModal = () => showModal({ id })

  return (
    <TableRow hover>
      <TableCell size={25} withEllipsis>
        <HoverablePopover
          trigger={
            <EllipsizedText onClick={showKPIModal} style={TEXT_STYLE}>
              {description}
            </EllipsizedText>
          }>
          {description}
        </HoverablePopover>
      </TableCell>
      <TableCell size={10}>{getEntityStatus(fieldValueMap, fields) || 'N/A'}</TableCell>
      <TableCell size={20} withEllipsis>
        <HoverablePopover
          trigger={
            <EllipsizedText>
              <Link
                href={{
                  pathname: `/plans/${plan.companyId}/edit/${plan.id}`,
                  query: {
                    ...(plan.parentPlanId && { parentId: plan.parentPlanId })
                  }
                }}>
                {plan.name}
              </Link>
            </EllipsizedText>
          }>
          {plan.name}
        </HoverablePopover>
      </TableCell>
      <TableCell size={5}>
        <Chip
          rounded
          icon={closestReview ? <ReviewIcon /> : null}
          chipColor={getChipColor(closestReview?.dueDate, closestReview?.status)}
          label={closestReview ? closestReview.dueDate.format('D MMM YY') : 'No review'}
        />
      </TableCell>
      <TableCell size={5}>
        <Chip
          rounded
          label={chipLabel}
          chipColor={chipColor}
          icon={closestRange ? <WatchLaterIcon style={ICON_STYLE} /> : null}
        />
      </TableCell>
      <TableCell size={15}>{lastResult || 'N/A'}</TableCell>
      <TableCell size={20}>{ownedBy}</TableCell>
    </TableRow>
  )
}

const getLastResult = (resultTarget: any, resultTypeScale: any, resultType: string) =>
  pipe(safeGetOr<any, any, any>([], 'capturedResults'), orderBy('id', 'asc'), last, (result: any = {}) => {
    let resultValue = result.value

    if (resultType === KPI_RESULT_TYPE_VALUES.SCALE || isUuid(resultValue)) {
      resultValue = pipe(
        find(({ id }) => id === result.value),
        getFp('label')
      )(resultTypeScale)
    }

    return resultValue
  })(resultTarget)

export default Row
